import Quill, { Delta, type Op } from 'quill';
import Embed from 'quill/blots/embed';
import type { AgentMissionBlock } from '../../../core/api/generated/models/AgentMissionBlock';
import type { AgentMissionDocument } from '../../../core/api/generated/models/AgentMissionDocument';
import type { AgentMissionReference } from '../../../core/api/generated/models/AgentMissionReference';
import type { BuilderMissionMention } from './agent-builder.types';

export type MissionEditorSnapshot = {
  document: AgentMissionDocument;
  text: string;
};

export type MissionBlockMoveDirection = 'up' | 'down';

export type MissionChipValue = {
  type: AgentMissionReference['type'];
  raw: string;
  targetId: string;
  label: string;
  tokenLabel: string;
  description: string;
};

export type MissionCommandTrigger = {
  kind: 'mention';
  trigger: '>' | '@' | '!' | '$';
  raw: string;
  query: string;
  fromOffset: number;
  mentionTypes: readonly AgentMissionReference['type'][];
};

let missionBlotsRegistered = false;

const MISSION_RAW_TOKEN_PATTERN =
  /(?:!tool|@library|@skill|>automation|#automation|!trigger|#stage|\?handoff|\$field):[a-zA-Z0-9_-]+(?:[._:-][a-zA-Z0-9_-]+)*/g;

class MissionChipBlot extends Embed {
  static override blotName = 'missionChip';
  static override tagName = 'span';
  static override className = 'mission-token';

  static override create(value: unknown): Node {
    const node = super.create(value) as HTMLElement;
    const chip = coerceMissionChipValue(value);
    node.classList.add(`mission-token--${chip.type}`);
    node.dataset['mention'] = chip.type;
    node.dataset['raw'] = chip.raw;
    node.dataset['targetId'] = chip.targetId;
    node.dataset['label'] = chip.label;
    node.dataset['tokenLabel'] = chip.tokenLabel;
    node.dataset['description'] = chip.description;
    node.setAttribute('title', chip.description || chip.raw);
    node.setAttribute('contenteditable', 'false');
    node.textContent = chip.tokenLabel || chip.label || chip.raw;
    return node;
  }

  static override value(node: HTMLElement): MissionChipValue {
    return {
      type: missionReferenceType(node.dataset['mention']),
      raw: node.dataset['raw'] || node.textContent || '',
      targetId: node.dataset['targetId'] || '',
      label: node.dataset['label'] || node.textContent || '',
      tokenLabel: node.dataset['tokenLabel'] || node.textContent || '',
      description: node.dataset['description'] || node.getAttribute('title') || '',
    };
  }
}

export function registerMissionQuillBlots(): void {
  if (missionBlotsRegistered) return;
  Quill.register(MissionChipBlot, true);
  missionBlotsRegistered = true;
}

export function missionDeltaFromDocument(
  document: AgentMissionDocument,
  mentions: readonly BuilderMissionMention[],
): Delta {
  const delta = new Delta();
  appendInline(delta, document.title || 'Missão do agente', mentions);
  delta.insert('\n', { header: 1 });

  if (document.summary.trim()) {
    appendInline(delta, document.summary.trim(), mentions);
    delta.insert('\n');
  }

  for (const block of document.blocks) {
    appendInline(delta, block.title || blockTypeLabel(block.type), mentions);
    delta.insert('\n', { header: 2 });
    for (const line of splitMissionLines(block.body || defaultBodyForBlockType(block.type))) {
      appendLine(delta, line, mentions);
    }
  }

  if (delta.length() === 0) {
    delta.insert('Missão do agente\n', { header: 1 });
  }

  return delta;
}

export function missionDocumentFromDelta(
  delta: Delta,
  fallbackTitle: string,
): MissionEditorSnapshot {
  const references: AgentMissionReference[] = [];
  const blocks: AgentMissionBlock[] = [];
  let title = fallbackTitle || 'Missão do agente';
  let summary = '';
  let currentBlock: EditableMissionBlock | null = null;
  let hasBlockHeading = false;

  delta.eachLine((line, attributes) => {
    const text = textFromDelta(line).trim();
    if (!text) return;
    const lineReferences = referencesFromDelta(line);
    lineReferences.forEach((reference) => upsertReference(references, reference));

    if (attributes?.['header'] === 1) {
      title = text;
      return;
    }

    if (attributes?.['header'] === 2) {
      if (currentBlock) blocks.push(blockFromEditor(currentBlock, blocks.length));
      hasBlockHeading = true;
      currentBlock = {
        title: text,
        type: 'stage',
        body: [],
        references: [...lineReferences],
      };
      return;
    }

    const renderedLine = linePrefix(attributes) + text;
    if (!summary) summary = renderedLine;
    if (!hasBlockHeading) return;
    if (!currentBlock) {
      currentBlock = {
        title: 'Instruções',
        type: 'note',
        body: [],
        references: [],
      };
    }
    currentBlock.body.push(renderedLine);
    lineReferences.forEach((reference) => upsertReference(currentBlock!.references, reference));
  });

  if (currentBlock) blocks.push(blockFromEditor(currentBlock, blocks.length));

  if (blocks.length === 0) {
    blocks.push({
      id: 'mission-block-1',
      type: 'stage',
      slug: 'instrucoes',
      title: 'Instrucoes operacionais',
      body: summary || 'Descreva como o agente deve conduzir a conversa, decidir proximos passos e acionar handoff quando necessario.',
      fields: {},
      references: [],
      diagnostics: [],
    });
  }

  const text = textFromDocumentDelta(delta);
  return {
    text,
    document: withLocalDiagnostics({
      schema: 'agent-mission-v1',
      version: 1,
      locale: 'pt-BR',
      title,
      summary: summary || text || title,
      blocks,
      references,
      diagnostics: [],
    }),
  };
}

export function missionChipValueFromMention(mention: BuilderMissionMention): MissionChipValue {
  return {
    type: mention.type,
    raw: mention.raw,
    targetId: mention.targetId,
    label: mention.label,
    tokenLabel: mention.tokenLabel,
    description: mention.description,
  };
}

export function missionChipValueFromRawToken(raw: string): MissionChipValue {
  return fallbackMissionChipValue(raw);
}

export function missionCommandTriggerFromText(textBefore: string): MissionCommandTrigger | null {
  const match = /(^|\s)([>@!$])([a-zA-Z0-9À-ÿ._:-]*)$/u.exec(textBefore);
  if (!match) return null;
  const trigger = match[2] as MissionCommandTrigger['trigger'];
  const query = match[3] ?? '';
  const raw = `${trigger}${query}`;
  return {
    kind: 'mention',
    trigger,
    raw,
    query,
    fromOffset: raw.length,
    mentionTypes: mentionTypesForTrigger(trigger, query),
  };
}

export function sanitizeMissionDelta(delta: Delta): Delta {
  const sanitized = new Delta();
  for (const op of delta.ops) {
    if (typeof op.insert === 'string') {
      sanitized.insert(op.insert, sanitizeAttributes(op.attributes));
      continue;
    }
    const chip = missionChipFromOp(op);
    if (chip) {
      sanitized.insert({ missionChip: chip });
    }
  }
  return sanitized;
}

export function moveMissionBlockInDelta(
  delta: Delta,
  index: number,
  direction: MissionBlockMoveDirection,
): { delta: Delta; selectionIndex: number } | null {
  const lines = missionDeltaLines(delta);
  if (lines.length === 0) return null;

  const targetLine = lineIndexForOffset(lines, index);
  const prefix: MissionDeltaLine[] = [];
  const groups: MissionDeltaLineGroup[] = [];
  let currentGroup: MissionDeltaLineGroup | null = null;
  let cursor = 0;

  for (const [lineIndex, line] of lines.entries()) {
    if (line.attributes?.['header'] === 2) {
      if (currentGroup) groups.push(currentGroup);
      currentGroup = {
        lines: [line],
        startLine: lineIndex,
        startIndex: cursor,
      };
    } else if (currentGroup) {
      currentGroup.lines.push(line);
    } else {
      prefix.push(line);
    }
    cursor += line.length;
  }

  if (currentGroup) groups.push(currentGroup);
  if (groups.length < 2) return null;

  const groupIndex = groups.findIndex(
    (group) => targetLine >= group.startLine && targetLine < group.startLine + group.lines.length,
  );
  if (groupIndex === -1) return null;

  const targetIndex = direction === 'up' ? groupIndex - 1 : groupIndex + 1;
  if (targetIndex < 0 || targetIndex >= groups.length) return null;

  const movingGroup = groups[groupIndex]!;
  const offsetWithinGroup = Math.max(0, index - movingGroup.startIndex);
  const reordered = [...groups];
  reordered.splice(groupIndex, 1);
  reordered.splice(targetIndex, 0, movingGroup);

  let rebuilt = new Delta();
  let selectionIndex = 0;
  cursor = 0;
  for (const line of prefix) {
    rebuilt = appendMissionDeltaLine(rebuilt, line);
    cursor += line.length;
  }
  for (const group of reordered) {
    if (group === movingGroup) {
      selectionIndex = cursor + Math.min(offsetWithinGroup, missionDeltaGroupLength(group) - 1);
    }
    for (const line of group.lines) {
      rebuilt = appendMissionDeltaLine(rebuilt, line);
      cursor += line.length;
    }
  }

  return { delta: rebuilt, selectionIndex };
}

export function missionDocumentText(document: AgentMissionDocument): string {
  return [
    document.title,
    document.summary,
    ...document.blocks.flatMap((block) => [block.title, block.body]),
  ]
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

function withLocalDiagnostics(document: AgentMissionDocument): AgentMissionDocument {
  const blocks = document.blocks.map((block) => {
    const diagnostics = block.diagnostics.filter((diagnostic) => !isParserStructureDiagnostic(diagnostic.code));
    const body = block.body.trim();
    if (!body) {
      diagnostics.push({
        severity: 'warning',
        code: 'empty_block_body',
        message: `Complete as instruções de "${block.title}".`,
        blockId: block.id,
      });
    }
    if (
      block.type === 'retrieval' &&
      !block.references.some((reference) => reference.type === 'library')
    ) {
      diagnostics.push({
        severity: 'info',
        code: 'retrieval_without_library',
        message: `Adicione uma biblioteca em "${block.title}" se esta etapa depender de conhecimento.`,
        blockId: block.id,
      });
    }
    if (
      block.type === 'handoff' &&
      !block.references.some((reference) => reference.type === 'automation')
    ) {
      diagnostics.push({
        severity: 'info',
        code: 'handoff_without_target',
        message: `Defina a automação de handoff em "${block.title}" quando ela já existir.`,
        blockId: block.id,
      });
    }
    if (
      block.type === 'trigger_suggestion' &&
      !block.references.some(
        (reference) => reference.type === 'trigger' || reference.type === 'automation',
      )
    ) {
      diagnostics.push({
        severity: 'info',
        code: 'trigger_without_reference',
        message: `Relacione um gatilho ou automação em "${block.title}" antes de aceitar em Deployments.`,
        blockId: block.id,
      });
    }
    return { ...block, diagnostics };
  });

  const diagnostics = document.diagnostics.filter((diagnostic) => !isParserStructureDiagnostic(diagnostic.code));
  if (!document.title.trim()) {
    diagnostics.push({
      severity: 'error',
      code: 'mission_title_required',
      message: 'A missão precisa de um título.',
    });
  }
  if (blocks.length === 0) {
    diagnostics.push({
      severity: 'error',
      code: 'mission_blocks_required',
      message: 'A missão precisa de pelo menos uma etapa operacional.',
    });
  }

  return { ...document, blocks, diagnostics };
}

function isParserStructureDiagnostic(code: string): boolean {
  return (
    code === 'mission_unmatched_end' ||
    code === 'mission_missing_blocks' ||
    code === 'mission_block_not_closed'
  );
}

function missionDeltaLines(delta: Delta): MissionDeltaLine[] {
  const lines: MissionDeltaLine[] = [];
  delta.eachLine((line, attributes) => {
    const sanitizedAttributes = sanitizeAttributes(attributes);
    lines.push({
      delta: sanitizeMissionDelta(new Delta(line.ops)),
      attributes: sanitizedAttributes,
      length: line.length() + 1,
    });
  });
  return lines;
}

function lineIndexForOffset(lines: readonly MissionDeltaLine[], index: number): number {
  let cursor = 0;
  for (const [lineIndex, line] of lines.entries()) {
    const end = cursor + line.length;
    if (index <= end) return lineIndex;
    cursor = end;
  }
  return Math.max(0, lines.length - 1);
}

function appendMissionDeltaLine(delta: Delta, line: MissionDeltaLine): Delta {
  const next = delta.concat(new Delta(line.delta.ops));
  next.insert('\n', line.attributes);
  return next;
}

function missionDeltaGroupLength(group: MissionDeltaLineGroup): number {
  return group.lines.reduce((total, line) => total + line.length, 0);
}

function appendLine(
  delta: Delta,
  line: MissionBodyLine,
  mentions: readonly BuilderMissionMention[],
): void {
  appendInline(delta, line.text, mentions);
  if (line.kind === 'subheading') {
    delta.insert('\n', { header: 3 });
  } else if (line.kind === 'code') {
    delta.insert('\n', { 'code-block': true });
  } else if (line.kind === 'bullet') {
    delta.insert('\n', { list: 'bullet' });
  } else if (line.kind === 'ordered') {
    delta.insert('\n', { list: 'ordered' });
  } else {
    delta.insert('\n');
  }
}

function appendInline(
  delta: Delta,
  value: string,
  mentions: readonly BuilderMissionMention[],
): void {
  let index = 0;
  for (const match of value.matchAll(MISSION_RAW_TOKEN_PATTERN)) {
    const raw = match[0];
    const matchIndex = match.index ?? 0;
    if (matchIndex > index) delta.insert(value.slice(index, matchIndex));
    const mention = mentions.find((item) => item.raw === raw);
    delta.insert({
      missionChip: mention ? missionChipValueFromMention(mention) : fallbackMissionChipValue(raw),
    });
    index = matchIndex + raw.length;
  }
  if (index < value.length) delta.insert(value.slice(index));
}

function textFromDelta(delta: Delta): string {
  return delta.ops.map(textFromOp).join('');
}

function textFromOp(op: Op): string {
  if (typeof op.insert === 'string') return op.insert;
  const chip = missionChipFromOp(op);
  return chip?.raw ?? '';
}

function textFromDocumentDelta(delta: Delta): string {
  const lines: string[] = [];
  delta.eachLine((line, attributes) => {
    const text = textFromDelta(line).trim();
    if (text) lines.push(linePrefix(attributes) + text);
  });
  return lines.join('\n\n').trim();
}

function referencesFromDelta(delta: Delta): AgentMissionReference[] {
  const references: AgentMissionReference[] = [];
  for (const op of delta.ops) {
    const chip = missionChipFromOp(op);
    if (chip) {
      upsertReference(references, {
        type: chip.type,
        raw: chip.raw,
        targetId: chip.targetId,
        label: chip.label,
        metadata: { source: 'agent_builder' },
      });
      continue;
    }
    if (typeof op.insert !== 'string') continue;
    for (const match of op.insert.matchAll(MISSION_RAW_TOKEN_PATTERN)) {
      const raw = match[0];
      const value = fallbackMissionChipValue(raw);
      upsertReference(references, {
        type: value.type,
        raw: value.raw,
        targetId: value.targetId,
        label: value.label,
        metadata: { source: 'agent_builder' },
      });
    }
  }
  return references;
}

function missionChipFromOp(op: Op): MissionChipValue | null {
  if (typeof op.insert !== 'object' || op.insert === null) return null;
  const value = (op.insert as Record<string, unknown>)['missionChip'];
  return value ? coerceMissionChipValue(value) : null;
}

function coerceMissionChipValue(value: unknown): MissionChipValue {
  const record = isRecord(value) ? value : {};
  const raw = readString(record, 'raw');
  return {
    type: missionReferenceType(readString(record, 'type')),
    raw,
    targetId: readString(record, 'targetId') || raw.split(':').slice(1).join(':'),
    label: readString(record, 'label') || raw,
    tokenLabel: readString(record, 'tokenLabel') || readString(record, 'label') || raw,
    description: readString(record, 'description') || 'Referência da missão',
  };
}

function fallbackMissionChipValue(raw: string): MissionChipValue {
  return {
    type: missionReferenceTypeFromRaw(raw),
    raw,
    targetId: raw.split(':').slice(1).join(':'),
    label: raw,
    tokenLabel: raw,
    description: 'Referência digitada',
  };
}

function sanitizeAttributes(attributes: Op['attributes']): Op['attributes'] {
  if (!attributes) return undefined;
  const next: Record<string, unknown> = {};
  if (attributes['bold']) next['bold'] = true;
  if (attributes['italic']) next['italic'] = true;
  if (attributes['header'] === 1 || attributes['header'] === 2 || attributes['header'] === 3) {
    next['header'] = attributes['header'];
  }
  if (attributes['code-block']) {
    next['code-block'] = attributes['code-block'];
  } else if (attributes['list'] === 'bullet' || attributes['list'] === 'ordered') {
    next['list'] = attributes['list'];
  }
  return Object.keys(next).length ? next : undefined;
}

function splitMissionLines(value: string): MissionBodyLine[] {
  const lines: MissionBodyLine[] = [];
  let insideCodeBlock = false;

  for (const rawLine of value.split(/\n+/)) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith('```')) {
      insideCodeBlock = !insideCodeBlock;
      const codeFenceText = line.replace(/^```\s*/u, '').trim();
      if (codeFenceText) lines.push({ text: codeFenceText, kind: 'code' });
      continue;
    }

    if (insideCodeBlock) {
      lines.push({ text: line, kind: 'code' });
      continue;
    }

    if (line.startsWith('### ')) {
      lines.push({ text: line.slice(4), kind: 'subheading' });
      continue;
    }
    if (line.startsWith('- ')) {
      lines.push({ text: line.slice(2), kind: 'bullet' });
      continue;
    }
    if (/^\d+\.\s+/u.test(line)) {
      lines.push({ text: line.replace(/^\d+\.\s+/u, ''), kind: 'ordered' });
      continue;
    }
    lines.push({ text: line, kind: null });
  }

  return lines;
}

function listPrefix(attributes?: Record<string, unknown>): string {
  if (attributes?.['list'] === 'bullet') return '- ';
  if (attributes?.['list'] === 'ordered') return '1. ';
  return '';
}

function linePrefix(attributes?: Record<string, unknown>): string {
  if (attributes?.['header'] === 3) return '### ';
  if (attributes?.['code-block']) return '``` ';
  return listPrefix(attributes);
}

function blockFromEditor(block: EditableMissionBlock, index: number): AgentMissionBlock {
  return {
    id: `mission-block-${index + 1}`,
    type: block.type,
    slug: slugify(block.title) || `etapa-${index + 1}`,
    title: block.title,
    body: block.body.join('\n'),
    fields: {},
    references: block.references,
    diagnostics: [],
  };
}

function blockTypeLabel(type: AgentMissionBlock['type']): string {
  const labels: Record<AgentMissionBlock['type'], string> = {
    stage: 'Etapa',
    trigger_suggestion: 'Sugestão de gatilho',
    retrieval: 'Consulta de conhecimento',
    collection: 'Coleta de dados',
    handoff: 'Transferência para humano',
    policy: 'Política de resposta',
    note: 'Nota',
  };
  return labels[type];
}

function defaultBodyForBlockType(type: AgentMissionBlock['type']): string {
  const bodies: Partial<Record<AgentMissionBlock['type'], string>> = {
    stage: 'Descreva objetivo, critérios e próximo passo desta etapa.',
    retrieval: 'Consulte as bibliotecas permitidas antes de responder quando houver dúvida factual.',
    collection: 'Colete apenas os dados necessários e confirme informações sensíveis antes de salvar.',
    handoff:
      'Transfira para humano em exceções, baixa confiança, risco comercial ou pedido fora do escopo.',
  };
  return bodies[type] ?? 'Descreva esta etapa.';
}

function missionReferenceType(value: string | undefined): AgentMissionReference['type'] {
  if (
    value === 'library' ||
    value === 'skill' ||
    value === 'tool' ||
    value === 'automation' ||
    value === 'field' ||
    value === 'trigger' ||
    value === 'stage' ||
    value === 'handoff'
  ) {
    return value;
  }
  return 'field';
}

function missionReferenceTypeFromRaw(raw: string): AgentMissionReference['type'] {
  if (raw.startsWith('@library:')) return 'library';
  if (raw.startsWith('@skill:')) return 'skill';
  if (raw.startsWith('!tool:')) return 'tool';
  if (raw.startsWith('>automation:')) return 'automation';
  if (raw.startsWith('#automation:')) return 'automation';
  if (raw.startsWith('$field:')) return 'field';
  if (raw.startsWith('!trigger:')) return 'trigger';
  if (raw.startsWith('#stage:')) return 'stage';
  if (raw.startsWith('?handoff:')) return 'handoff';
  return 'field';
}

function mentionTypesForTrigger(
  trigger: MissionCommandTrigger['trigger'],
  query: string,
): readonly AgentMissionReference['type'][] {
  if (trigger === '>') return ['automation'];
  if (trigger === '@') {
    if (query.startsWith('library:')) return ['library'];
    if (query.startsWith('skill:')) return ['skill'];
    return ['library', 'skill'];
  }
  if (trigger === '!') {
    if (query.startsWith('tool:')) return ['tool'];
    return ['tool'];
  }
  if (trigger === '$') return ['field'];
  return [];
}

function upsertReference(
  references: AgentMissionReference[],
  reference: AgentMissionReference,
): void {
  if (!reference.raw || !reference.targetId) return;
  if (
    references.some((item) => item.type === reference.type && item.targetId === reference.targetId)
  ) {
    return;
  }
  references.push(reference);
}

function slugify(value: string): string {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

type EditableMissionBlock = {
  title: string;
  type: AgentMissionBlock['type'];
  body: string[];
  references: AgentMissionReference[];
};

type MissionBodyLine = {
  text: string;
  kind: 'bullet' | 'ordered' | 'subheading' | 'code' | null;
};

type MissionDeltaLine = {
  delta: Delta;
  attributes: Op['attributes'];
  length: number;
};

type MissionDeltaLineGroup = {
  lines: MissionDeltaLine[];
  startLine: number;
  startIndex: number;
};
